// Display · Avatar
//
// Initials-based circle avatar. Deterministic tone from a key, optional
// image URL for future photo support. Pure visual atom — no events.
//
// All visual values come from `@ds/tokens/palette`.

import {
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  TEXT,
  TONE,
  type ToneName,
} from "@ds/tokens/palette";
import { avatarInitials } from "@ds/display/Avatar.helpers";

// ─── Size scale ──────────────────────────────────────────────────────────────

/** Diameter (px) per size name. Off-scale values: sm=22, md=28, lg=36.
 *  These three sizes are empirically matched to the three usage contexts:
 *  sm=22 — table/chip rows (dense); md=28 — comment threads, topbar;
 *  lg=36 — entity-header hero slots. No token in CONTROL_HEIGHT matches
 *  these diameters exactly (24/32/40 skew large for an avatar circle), so
 *  the values are documented here as avatar-specific constants. */
const AVATAR_SIZE_PX: Record<AvatarSize, number> = {
  sm: 22,
  md: 28,
  lg: 36,
};

/** Font size (px) per avatar size. Empirically tuned initials legibility:
 *  sm=10 (FONT_SIZE.micro), md=11 (FONT_SIZE.label), lg=13 (FONT_SIZE.body). */
const AVATAR_FONT_PX: Record<AvatarSize, number> = {
  sm: FONT_SIZE.micro,
  md: FONT_SIZE.label,
  lg: FONT_SIZE.body,
};

// ─── Inputs ──────────────────────────────────────────────────────────────────

export type AvatarSize = "sm" | "md" | "lg";

export interface AvatarInput {
  /**
   * User display name — auto-generates initials if `initials` is not provided.
   * "Marek Nowak" → "MN", "Marek" → "M", "" → "?".
   */
  name?: string;
  /**
   * Explicit initials override. Use when you already have them pre-computed.
   * Max 2 characters. Takes precedence over `name`.
   */
  initials?: string;
  /** Image URL. When provided, renders an `<img>` inside the circle instead of initials. */
  src?: string;
  /**
   * Semantic tone — drives the background color from `TONE[tone].base`.
   * Default: `"grey"` (neutral).
   */
  tone?: ToneName;
  /**
   * Size scale.
   * - `"sm"` — 22 px diameter, font 10 px. Dense rows, table cells.
   * - `"md"` (default) — 28 px diameter, font 11 px. Threads, topbar.
   * - `"lg"` — 36 px diameter, font 13 px. Entity-header hero slots.
   */
  size?: AvatarSize;
  /** `data-testid` on the root `<span>`. Atom — no inner testids auto-emitted. */
  testId?: string;
}

// Atom: no Output (no events), no Actions. Interfaces dropped per @ds Rule 3.

export type AvatarProps = AvatarInput;

// ─── Implementation ──────────────────────────────────────────────────────────

export function Avatar({
  name,
  initials,
  src,
  tone = "grey",
  size = "md",
  testId,
}: AvatarProps) {
  const px   = AVATAR_SIZE_PX[size];
  const fsPx = AVATAR_FONT_PX[size];
  const text = initials ?? avatarInitials(name);
  const bg   = TONE[tone].base;

  return (
    <span
      data-testid={testId}
      style={{
        display:        "inline-flex",
        alignItems:     "center",
        justifyContent: "center",
        width:          px,
        height:         px,
        borderRadius:   "50%",
        background:     bg,
        color:          TEXT.inverse, // always white-on-tone
        fontSize:       fsPx,
        fontWeight:     FONT_WEIGHT.medium,
        fontFamily:     FONT_FAMILY.sans,
        flexShrink:     0,
        userSelect:     "none",
        overflow:       "hidden",
        position:       "relative",
      }}
    >
      {src ? (
        <img
          src={src}
          alt={name ?? ""}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        text
      )}
    </span>
  );
}
